import React, {Component} from "react";
import withStyles from '@mui/styles/withStyles';
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grow from "@mui/material/Grow";
import Collapse from "@mui/material/Collapse";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ButtonBase from "@mui/material/ButtonBase";
import {AttributeValuesPrettier} from "../../../core/prettifier/AttributeValuesPrettier";
import prStyleRegistry from "../../../core/customization/prStyleRegistry";

const DEFAULT_CONFETTI_COLORS = ['#FFD700', '#FFA000', '#FF6F00', '#FFECB3', '#FFF8E1', '#E65100'];
const CONFETTI_COUNT = 60;

const useStyles = theme => ({
    container: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 50%, #FF8F00 100%)',
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
        boxShadow: '0 4px 20px rgba(255, 165, 0, 0.4)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },
    title: {
        color: '#3E2723',
        fontWeight: 800,
        letterSpacing: '0.05em',
        textShadow: '0 1px 2px rgba(255, 255, 255, 0.3)',
    },
    trophy: {
        fontSize: '2rem',
        color: '#3E2723',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    },
    summaryText: {
        color: '#4E342E',
        fontWeight: 600,
        fontSize: '0.8rem',
        textAlign: 'center',
        marginTop: theme.spacing(0.5),
    },
    expandButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: theme.spacing(0.5, 0),
        borderRadius: 8,
        color: '#4E342E',
        marginTop: theme.spacing(0.75),
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
        },
    },
    expandIcon: {
        fontSize: '1.2rem',
        color: '#4E342E',
    },
    expandLabel: {
        color: '#4E342E',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    prItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
        borderRadius: 8,
        padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
        marginBottom: theme.spacing(0.75),
        backdropFilter: 'blur(4px)',
    },
    exerciseName: {
        color: '#3E2723',
        fontWeight: 700,
        fontSize: '0.85rem',
    },
    prTypeLabel: {
        color: '#4E342E',
        fontWeight: 500,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
    },
    valueSection: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    oldValue: {
        color: 'rgba(62, 39, 35, 0.55)',
        textDecoration: 'line-through',
        fontSize: '0.8rem',
    },
    arrow: {
        color: '#2E7D32',
        fontSize: '1rem',
    },
    newValue: {
        color: '#1B5E20',
        fontWeight: 800,
        fontSize: '0.95rem',
    },
    detailsContainer: {
        marginTop: theme.spacing(1),
    },
    confettiPiece: {
        position: 'absolute',
        width: 8,
        height: 8,
        borderRadius: '50%',
        opacity: 0,
        animation: '$confettiFall 3s ease-out forwards',
    },
    '@keyframes confettiFall': {
        '0%': {
            opacity: 1,
            transform: 'translateY(-20px) rotate(0deg) scale(0)',
        },
        '10%': {
            opacity: 1,
            transform: 'translateY(0px) rotate(45deg) scale(1)',
        },
        '100%': {
            opacity: 0,
            transform: 'translateY(200px) rotate(720deg) scale(0.3)',
        },
    },
    '@keyframes pulse': {
        '0%, 100%': {
            transform: 'scale(1)',
        },
        '50%': {
            transform: 'scale(1.05)',
        },
    },
    pulseContainer: {
        animation: '$pulse 2s ease-in-out 1',
    },
});

class PersonalRecordBanner extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false,
        };
        this.confettiPieces = this.generateConfetti();
    }

    getCustomStyle() {
        const { prStyleId } = this.props;
        if (prStyleId && prStyleRegistry[prStyleId]) {
            return prStyleRegistry[prStyleId];
        }
        return null;
    }

    generateConfetti() {
        const customStyle = this.getCustomStyle();
        const colors = customStyle?.confettiColors || DEFAULT_CONFETTI_COLORS;
        return Array.from({length: CONFETTI_COUNT}, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 1.5,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: 4 + Math.random() * 8,
            shape: Math.random() > 0.5 ? '50%' : '2px',
        }));
    }

    deduplicatePRs(personalRecords) {
        const seen = {};
        return personalRecords.filter(pr => {
            const key = `${pr.exerciseId}_${pr.prType}`;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
        });
    }

    getPrTypeLabel(prType) {
        switch (prType) {
            case 'weight': return 'Weight PR';
            case 'reps': return 'Reps PR';
            case 'reps at weight': return 'Reps at Weight PR';
            case 'volume': return 'Volume PR';
            case 'distance': return 'Distance PR';
            case 'time': return 'Time PR';
            default: return 'PR';
        }
    }

    formatLogValues(pr, values) {
        if (!values || !pr.attributeKeys) return null;
        const fakeType = {
            attributes: pr.attributeKeys.map(key => ({key, name: key})),
        };
        const fakeLog = {values};
        return AttributeValuesPrettier.prettifyAttributeValues(fakeType, fakeLog);
    }

    buildSummaryText(dedupedPRs) {
        const exerciseNames = [...new Set(dedupedPRs.map(pr => pr.exerciseName))];
        if (exerciseNames.length <= 2) {
            return exerciseNames.join(' & ');
        }
        return `${exerciseNames[0]} & ${exerciseNames.length - 1} more`;
    }

    toggleExpanded = () => {
        this.setState(prev => ({expanded: !prev.expanded}));
    }

    render() {
        const {classes, personalRecords, animated} = this.props;
        const {expanded} = this.state;

        if (!personalRecords || personalRecords.length === 0) {
            return null;
        }

        const dedupedPRs = this.deduplicatePRs(personalRecords);

        const customStyle = this.getCustomStyle();
        const containerOverride = customStyle?.containerStyle || {};
        const textColor = customStyle?.textColor;

        return (
            <Grow in={true} timeout={600}>
                <div
                    className={`${classes.container} ${animated ? classes.pulseContainer : ''} ${customStyle?.shake ? classes.pulseContainer : ''}`}
                    style={containerOverride}
                >
                    {animated && this.confettiPieces.map(piece => (
                        <div
                            key={piece.id}
                            className={classes.confettiPiece}
                            style={{
                                left: `${piece.left}%`,
                                animationDelay: `${piece.delay}s`,
                                backgroundColor: piece.color,
                                width: piece.size,
                                height: piece.size,
                                borderRadius: piece.shape,
                            }}
                        />
                    ))}

                    <div className={classes.header}>
                        <EmojiEventsIcon className={classes.trophy} style={textColor ? {color: textColor} : {}}/>
                        <Typography variant="h6" className={classes.title} style={textColor ? {color: textColor} : {}}>
                            NEW PR{dedupedPRs.length > 1 ? 's' : ''}!
                        </Typography>
                        <EmojiEventsIcon className={classes.trophy} style={textColor ? {color: textColor} : {}}/>
                    </div>

                    <Typography className={classes.summaryText} style={textColor ? {color: textColor} : {}}>
                        {this.buildSummaryText(dedupedPRs)}
                    </Typography>

                    <ButtonBase className={classes.expandButton} onClick={this.toggleExpanded} style={textColor ? {color: textColor} : {}}>
                        <Typography className={classes.expandLabel} style={textColor ? {color: textColor} : {}}>
                            {expanded ? 'Hide' : 'View'} details
                        </Typography>
                        {expanded
                            ? <ExpandLessIcon className={classes.expandIcon} style={textColor ? {color: textColor} : {}}/>
                            : <ExpandMoreIcon className={classes.expandIcon} style={textColor ? {color: textColor} : {}}/>
                        }
                    </ButtonBase>

                    <Collapse in={expanded}>
                        <div className={classes.detailsContainer}>
                            {dedupedPRs.map((pr, index) => (
                                <Grow
                                    key={`${pr.exerciseId}_${pr.prType}`}
                                    in={expanded}
                                    timeout={300 + (index * 100)}
                                >
                                    <div className={classes.prItem}>
                                        <Box flex={1}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography className={classes.exerciseName}>
                                                    {pr.exerciseName}
                                                </Typography>
                                                <Typography className={classes.prTypeLabel}>
                                                    {this.getPrTypeLabel(pr.prType)}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                                {pr.oldValues && (
                                                    <Typography className={classes.oldValue}>
                                                        {this.formatLogValues(pr, pr.oldValues)}
                                                    </Typography>
                                                )}
                                                <ArrowUpwardIcon className={classes.arrow} style={customStyle?.newValueColor ? {color: customStyle.newValueColor} : {}}/>
                                                <Typography className={classes.newValue} style={customStyle?.newValueColor ? {color: customStyle.newValueColor} : {}}>
                                                    {this.formatLogValues(pr, pr.newValues)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </div>
                                </Grow>
                            ))}
                        </div>
                    </Collapse>
                </div>
            </Grow>
        );
    }
}

export default withStyles(useStyles)(PersonalRecordBanner);
